import { ReadonlyMat3, ReadonlyVec3, vec3 } from 'gl-matrix';

/**
 * The unit patient axes in x, y, z order (left-right, anterior-posterior,
 * superior-inferior).
 */
export const PATIENT_AXES: readonly [ReadonlyVec3, ReadonlyVec3, ReadonlyVec3] =
    [
        [1, 0, 0],
        [0, 1, 0],
        [0, 0, 1],
    ];

/**
 * The angle in degrees between `direction` and the nearest of `axes`,
 * either sign: 0 when it lies along one, at most 54.7 between three
 * orthogonal axes. Throws for a zero-length direction, or for no axes.
 */
export function angleToNearestAxisDeg(
    direction: ReadonlyVec3,
    axes: ReadonlyArray<ReadonlyVec3>
): number {
    const length = vec3.length(direction);
    if (!(length > 0) || axes.length === 0) {
        throw new Error(
            'angleToNearestAxisDeg: needs a non-zero direction and an axis'
        );
    }
    const maxAbsDot = Math.max(
        ...axes.map(
            (axis) =>
                Math.abs(vec3.dot(direction, axis)) /
                (length * vec3.length(axis))
        )
    );
    // Clamp before acos to guard against tiny FP excursions past 1.
    const clamped = Math.min(1, Math.max(-1, maxAbsDot));
    return (Math.acos(clamped) * 180) / Math.PI;
}

/**
 * Compute the deviation in degrees of a slice's normal vector
 * from the closest principal patient axis. Returns 0 for axis-
 * aligned (transversal / coronal / sagittal) acquisitions and the
 * deviation angle for oblique ones.
 *
 * Uses the convention that the slice normal lives in the third
 * column of the orientation matrix (matching DicomImageSlice and
 * Grid in this project).
 */
export function obliquityAngleDeg(orientation: ReadonlyMat3): number {
    // Slice normal = column 2 of the column-major mat3.
    const normal = vec3.fromValues(
        orientation[6],
        orientation[7],
        orientation[8]
    );
    return angleToNearestAxisDeg(normal, PATIENT_AXES);
}

/**
 * How far the axes of `orientation` lie off the nearest patient axes, in
 * degrees: the largest angle of its three axes, so an orientation tilted out
 * of its plane and one rotated within it both read their angle. 0 when the
 * axes are the patient axes.
 */
export function axesTiltDeg(orientation: ReadonlyMat3): number {
    // The axes are the columns of the column-major mat3.
    return Math.max(
        ...[0, 3, 6].map((column) =>
            angleToNearestAxisDeg(
                vec3.fromValues(
                    orientation[column],
                    orientation[column + 1],
                    orientation[column + 2]
                ),
                PATIENT_AXES
            )
        )
    );
}

/**
 * {@link axesTiltDeg} of the grid's orientation; undefined when the grid's
 * axes are the patient axes.
 */
export function sliceTiltDeg(grid: {
    isAxisAligned(): boolean;
    getOrientation(): ReadonlyMat3;
}): number | undefined {
    return grid.isAxisAligned()
        ? undefined
        : axesTiltDeg(grid.getOrientation());
}

/**
 * `12.3` -> `"12.3"`: an angle in degrees to one decimal place, with no
 * unit, so the message that shows it carries the `°`.
 *
 * `Number.prototype.toFixed` preserves the sign of the input even when the
 * rounded magnitude is zero, so `(-0.03).toFixed(1) === '-0.0'`, which
 * would read as a false negative rotation on a clinically meaningful angle.
 * Round first and normalise a rounded-to-zero result to positive zero.
 */
export function formatAngle(degrees: number): string {
    const rounded = Number(degrees.toFixed(1));
    return (rounded === 0 ? 0 : rounded).toFixed(1);
}
